import { API, deleteRequest, getRequest, getRequestBlob, postRequest, putRequest } from '@/api/api';

import type {
  BudgetsList,
  BudgetsListDto,
  BulkUpsertBudgetValues,
  SingleBudget,
  SingleBudgetDto,
  UpdateBudgetValues,
  UpsertBudgetValues,
} from './budget.dto';
import {
  mapBulkUpsertBudgetsToDto,
  mapDtoToBudget,
  mapUpdateBudgetToDto,
  mapUpsertBudgetToDto,
} from './budget.mappers';

export const getBudgets = async (
  page: number,
  limit: number,
  year?: number,
): Promise<BudgetsList> => {
  const { data } = await getRequest<BudgetsListDto>(API.EXPENSES, '/budget', false, {
    page,
    limit,
    ...(year ? { year } : {}),
  });
  return {
    count: data.count,
    items: (data.items ?? []).map(mapDtoToBudget),
  };
};

export const getBudgetById = async (id: string): Promise<SingleBudget> => {
  const { data } = await getRequest<SingleBudgetDto>(API.EXPENSES, `/budget/${id}`);
  return mapDtoToBudget(data);
};

export const upsertBudget = async (values: UpsertBudgetValues): Promise<SingleBudget> => {
  const { data } = await postRequest<SingleBudgetDto, ReturnType<typeof mapUpsertBudgetToDto>>(
    API.EXPENSES,
    '/budget',
    mapUpsertBudgetToDto(values),
  );
  return mapDtoToBudget(data);
};

export const updateBudget = async (
  id: string,
  values: UpdateBudgetValues,
): Promise<SingleBudget> => {
  const { data } = await putRequest<SingleBudgetDto, ReturnType<typeof mapUpdateBudgetToDto>>(
    API.EXPENSES,
    `/budget/${id}`,
    mapUpdateBudgetToDto(values),
  );
  return mapDtoToBudget(data);
};

export const deleteBudget = async (id: string): Promise<void> => {
  await deleteRequest(API.EXPENSES, `/budget/${id}`);
};

export const exportBudgetPdf = async (year: number): Promise<Blob> =>
  getRequestBlob(API.EXPENSES, '/budget/export/pdf', { year });

export const bulkUpsertYearBudgets = async (
  values: BulkUpsertBudgetValues,
): Promise<SingleBudget[]> => {
  const { data } = await putRequest<
    SingleBudgetDto[],
    ReturnType<typeof mapBulkUpsertBudgetsToDto>
  >(API.EXPENSES, `/budget/year/${values.year}`, mapBulkUpsertBudgetsToDto(values));
  return (data ?? []).map(mapDtoToBudget);
};
